import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Badge } from 'components/Badge/Badge'
import Chevron from 'assets/icons/Chevron'
import { EditIcon } from 'assets/icons/EditIcon'
import { useTheme } from 'styled-components'
import { ColorVariant, Flex } from 'styling/GlobalStyles'
import { MediumText, SmallText, TinyText } from 'styling/TypographyV2'
import { EditSiteModal } from './EditSiteModal'
import { BuildingIcon } from '../icons'
import {
  IOperationalOrganogramTeam,
  IOperationalOrgUser,
  IOperationalSite,
  IOperationalTeamSupervisor,
  SiteStatus,
  TEAM_TYPE_LABEL,
  TeamType,
} from 'features/operationalManagement/operationalManagement.types'
import { getOperationalSiteOrganogram } from 'features/operationalManagement/operationalManagementSlice'
import {
  DepartmentLabel,
  OutlineActionButton,
  PeopleCell,
  RoleChip,
  RoleChipsWrap,
  SiteAccordionCard,
  SiteAccordionHeader,
  SiteBodySection,
  SiteIconBadge,
  TeamRow,
  TeamRowLabel,
} from '../Organogram.styled'

const formatSupervisors = (supervisors: IOperationalTeamSupervisor[]): string => {
  if (supervisors.length === 0) return 'Unassigned'
  if (supervisors.length === 1) {
    const u = supervisors[0].user
    return `${u.firstName} ${u.lastName}`
  }
  const first = supervisors[0].user
  return `${first.firstName} ${first.lastName[0] ?? ''}, +${supervisors.length - 1}`
}

const countByRole = (members: IOperationalOrgUser[]): { roleName: string; count: number }[] => {
  const map = new Map<string, number>()
  for (const m of members) {
    const key = m.currentRole?.name ?? 'Unassigned role'
    map.set(key, (map.get(key) ?? 0) + 1)
  }
  return Array.from(map.entries())
    .map(([roleName, count]) => ({ roleName, count }))
    .sort((a, b) => b.count - a.count)
}

const flattenTeams = (nodes: IOperationalOrganogramTeam[]): IOperationalOrganogramTeam[] => {
  const out: IOperationalOrganogramTeam[] = []
  const walk = (n: IOperationalOrganogramTeam) => {
    out.push(n)
    for (const c of n.children) walk(c)
  }
  for (const n of nodes) walk(n)
  return out
}

const groupByType = (
  teams: IOperationalOrganogramTeam[],
): { type: TeamType; teams: IOperationalOrganogramTeam[] }[] => {
  const map = new Map<TeamType, IOperationalOrganogramTeam[]>()
  for (const t of teams) {
    const list = map.get(t.type) ?? []
    list.push(t)
    map.set(t.type, list)
  }
  return Array.from(map.entries())
    .map(([type, ts]) => ({ type, teams: ts }))
    .sort((a, b) => a.type - b.type)
}

interface SiteAccordionProps {
  site: IOperationalSite
  onUpdated?: () => void
}

export const SiteAccordion = ({ site, onUpdated }: SiteAccordionProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const theme = useTheme() as any
  const [open, setOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [fetchFailed, setFetchFailed] = useState(false)

  const organogram = useSelector(
    (state: RootState) => state.operationalManagement.organogramBySiteId[site.id],
  )
  const loading = useSelector((state: RootState) =>
    state.operationalManagement.loadingOrganogramSiteIds.includes(site.id),
  )

  const fetchOrganogram = () => {
    setFetchFailed(false)
    dispatch(getOperationalSiteOrganogram(site.id)).then((res) => {
      if (getOperationalSiteOrganogram.rejected.match(res)) setFetchFailed(true)
    })
  }

  useEffect(() => {
    if (open && !organogram && !loading && !fetchFailed) {
      fetchOrganogram()
    }
  }, [open, organogram, loading, fetchFailed, dispatch, site.id])

  const isActive = site.status === SiteStatus.Active
  const statusColor = isActive ? theme.colorGreen : theme.greyText
  const grouped = organogram ? groupByType(flattenTeams(organogram.teams)) : []

  return (
    <SiteAccordionCard>
      <SiteAccordionHeader
        role='button'
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${site.name} — ${open ? 'collapse' : 'expand'}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
      >
        <SiteIconBadge>
          <BuildingIcon />
        </SiteIconBadge>
        <Flex column gap='0.125rem' width='100%'>
          <Flex center gap='0.5rem'>
            <MediumText bold>{site.name}</MediumText>
            <Badge hexColor={statusColor}>{isActive ? 'Active' : 'Inactive'}</Badge>
          </Flex>
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            {site.teamCount} {site.teamCount === 1 ? 'team' : 'teams'} · {site.memberCount}{' '}
            assigned
          </SmallText>
        </Flex>
        <Flex center gap='0.5rem'>
          <MediumText colorVariant={ColorVariant.SECONDARY}>
            {site.memberCount} people
          </MediumText>
          <div
            role='button'
            tabIndex={0}
            aria-label='Edit site'
            title='Edit site'
            onClick={(e) => {
              e.stopPropagation()
              setEditOpen(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                setEditOpen(true)
              }
            }}
            style={{ display: 'flex', cursor: 'pointer' }}
          >
            <EditIcon />
          </div>
          <Chevron open={open} gray={false} />
        </Flex>
      </SiteAccordionHeader>

      {editOpen && (
        <EditSiteModal
          site={site}
          onClose={() => setEditOpen(false)}
          onUpdated={onUpdated}
        />
      )}

      {open && (
        <SiteBodySection>
          {loading && !organogram && (
            <SmallText colorVariant={ColorVariant.SECONDARY}>
              Loading organogram…
            </SmallText>
          )}
          {fetchFailed && !loading && !organogram && (
            <Flex center gap='0.75rem'>
              <SmallText colorVariant={ColorVariant.ERROR}>
                Failed to load organogram.
              </SmallText>
              <OutlineActionButton
                accent
                type='button'
                onClick={(e) => {
                  e.stopPropagation()
                  fetchOrganogram()
                }}
              >
                Retry
              </OutlineActionButton>
            </Flex>
          )}
          {organogram && grouped.length === 0 && (
            <SmallText colorVariant={ColorVariant.SECONDARY}>
              No teams yet.
            </SmallText>
          )}
          {grouped.map(({ type, teams }) => (
            <div key={type}>
              <DepartmentLabel>{TEAM_TYPE_LABEL[type]}</DepartmentLabel>
              <Flex column gap='0.5rem' mt='8'>
                {teams.map((team) => {
                  const roleCounts = countByRole(team.members)
                  return (
                    <TeamRow key={team.id}>
                      <TeamRowLabel>
                        <TinyText uppercase colorVariant={ColorVariant.SECONDARY}>
                          Team
                        </TinyText>
                        <MediumText bold>{team.name}</MediumText>
                      </TeamRowLabel>
                      <TeamRowLabel>
                        <TinyText uppercase colorVariant={ColorVariant.SECONDARY}>
                          {team.supervisors.length > 1 ? 'Supervisors' : 'Supervisor'}
                        </TinyText>
                        <MediumText>{formatSupervisors(team.supervisors)}</MediumText>
                      </TeamRowLabel>
                      <RoleChipsWrap>
                        {roleCounts.length === 0 ? (
                          <SmallText colorVariant={ColorVariant.SECONDARY}>
                            No members
                          </SmallText>
                        ) : (
                          roleCounts.map((rc) => (
                            <RoleChip key={rc.roleName}>
                              {rc.roleName} ×{rc.count}
                            </RoleChip>
                          ))
                        )}
                      </RoleChipsWrap>
                      <PeopleCell>
                        <TinyText uppercase colorVariant={ColorVariant.SECONDARY}>
                          People
                        </TinyText>
                        <MediumText bold>{team.members.length}</MediumText>
                      </PeopleCell>
                    </TeamRow>
                  )
                })}
              </Flex>
            </div>
          ))}
        </SiteBodySection>
      )}
    </SiteAccordionCard>
  )
}
